import './process-compare.css'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ConfirmModal, Input, Spinner } from '../../../Components'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { reexecuteUnit } from '../Editor/Functions'
import { FormView } from './FormView'
import { searchInView } from './Functions'
import { JsonView } from './JsonView'

const language = cap.language.state || 'IT'
const t = translations[language]

/**
 * Pagina confronto initial/final state degli step di un processo.
 * @returns {JSX.Element} Componente compare processi
 */
export const ProcessCompare = () => {
  const { processId } = useParams()
  const navigate = useNavigate()
  const { data: processSteps = [], isSuccess } = useCQuery(['processSteps', processId])
  const [stateIndex, setStateIndex] = useState(0)
  const [searchString, setSearchString] = useState('')
  const [numberSearch, setNumberSearch] = useState(0)
  const [totalSearch, setTotalSearch] = useState(0)
  const [jsonView, setJsonView] = useState(true)
  const [reexecuteModalVisible, setReexecuteModalVisible] = useState(false)

  const scrollIntoView = () =>
    setTimeout(() => setTotalSearch(searchInView(searchString, numberSearch) || 0), 10)

  useEffect(() => {
    setNumberSearch(0)
    scrollIntoView()
  }, [searchString, stateIndex])

  useEffect(() => {
    scrollIntoView()
  }, [numberSearch])

  if (!isSuccess) return <Spinner />

  const handleReexecute = () =>
    reexecuteUnit(processSteps[stateIndex]?.unit, processSteps[stateIndex]?.initialState)

  return (
    <div className="process-compare-page">
      <ConfirmModal
        visible={[reexecuteModalVisible, setReexecuteModalVisible]}
        header={t.pfRieseguiUnit}
        label={t.pfConfermaRieseguiUnit}
        onConfirm={handleReexecute}
      />

      <div className="process-compare-toolbar">
        <div className="process-compare-toolbar__actions">
          <button type="button" className="nbtn" onClick={() => navigate('/process/' + processId)}>
            <i className="pi pi-chevron-left" style={{ fontSize: 14 }} />
            {t.tornaIndietro}
          </button>
          <button type="button" className="nbtn" onClick={() => setJsonView(!jsonView)}>
            <i className={`pi ${jsonView ? 'pi-list' : 'pi-code'}`} style={{ fontSize: 14 }} />
            {jsonView ? t.pfVistaForm : t.pfVistaJson}
          </button>
          <button type="button" className="nbtn" onClick={() => setReexecuteModalVisible(true)}>
            <i className="pi pi-play" style={{ fontSize: 14 }} />
            {t.pfRieseguiUnit}
          </button>
        </div>
      </div>

      <div className="process-compare-step">
        <div className="process-compare-step__selector">
          <button
            type="button"
            className="nbtn nbtn--icon"
            disabled={stateIndex === 0}
            onClick={() => setStateIndex(stateIndex - 1)}
          >
            <i className="pi pi-chevron-left" />
          </button>
          <div className="process-compare-step__dropdown">
            <Input
              dropdown
              id="unit"
              value={processSteps[stateIndex]?.unit}
              options={processSteps.map(({ unit }) => unit)}
              onChange={({ unit: value }) =>
                setStateIndex(processSteps.findIndex(({ unit }) => unit === value))
              }
            />
          </div>
          <button
            type="button"
            className="nbtn nbtn--icon"
            disabled={stateIndex === processSteps.length - 1}
            onClick={() => setStateIndex(stateIndex + 1)}
          >
            <i className="pi pi-chevron-right" />
          </button>
        </div>

        {jsonView && (
          <div className="process-compare-step__search">
            <div className="nsearch process-compare-search">
              <i className="pi pi-search" style={{ fontSize: 15, color: 'var(--ink-500)' }} />
              <input
                type="text"
                placeholder={t.pfCerca}
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setNumberSearch((numberSearch + 1) % totalSearch)
                }}
              />
            </div>
            {!!totalSearch && (
              <div className="process-compare-search-nav">
                <span>{`${numberSearch + 1}/${totalSearch}`}</span>
                <button
                  type="button"
                  className="nbtn nbtn--icon nbtn--sm"
                  disabled={totalSearch === 0}
                  title={t.pfRisultatoPrecedente}
                  onClick={() => setNumberSearch((numberSearch - 1 + totalSearch) % totalSearch)}
                >
                  <i className="pi pi-chevron-up" />
                </button>
                <button
                  type="button"
                  className="nbtn nbtn--icon nbtn--sm"
                  disabled={totalSearch === 0}
                  title={t.pfRisultatoSuccessivo}
                  onClick={() => setNumberSearch((numberSearch + 1) % totalSearch)}
                >
                  <i className="pi pi-chevron-down" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {jsonView ? <JsonView stateIndex={stateIndex} /> : <FormView stateIndex={stateIndex} />}
    </div>
  )
}
